import { useRef } from 'react'
import styled from 'styled-components'

import useDocumentClick from 'hooks/useDocumentClick'
import { media } from 'styles/media'

import InvisibleButton from '../components/display/InvisibleButton'
import AccommodationIcon from '../components/icons/Cabin'
import AdventureIcon from '../components/icons/Experience'

const Panel = styled.div<{ open: boolean }>`
  transition: opacity 150ms linear;
  opacity: ${({ open }) => (open ? '1' : '0')};
  visibility: ${({ open }) => (open === true ? 'visible' : 'hidden')};

  box-shadow: 0 8px 16px 2px rgb(0 0 0 / 10%);
  width: 100%;
  padding: 30px 0;
  border-bottom: 1px solid ${({ theme }) => theme?.['-ds-neutral-400']?.value};
  top: 65px;

  display: flex;
  justify-content: center;

  position: absolute;
  background-color: ${({ theme }) => theme?.['-ds-neutral-100']?.value};
  z-index: 4;

  ${media.md`
    justify-content: flex-start;
    width: fit-content;
    left: 20px;
    top: 80px;
    border: 1px solid ${({ theme }) => theme?.['-ds-neutral-400']?.value};
    border-radius: 8px;
  `}
`

export type SearchType = Extract<
  GQL_ListingTypes,
  'accommodation' | 'adventures'
>

const OptionButton = styled(InvisibleButton)`
  text-align: center;
  p {
    margin: 20px 0 0 0;
  }
`

const BaseRoundIcon = styled.div<{
  active?: boolean
  searchType?: GQL_ListingTypes
}>`
  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;
  color: ${({ theme }) => theme.brand.forest};

  ${({ active, searchType, theme }) => {
    switch (searchType) {
      case 'adventures':
        return `
          border: 1px solid ${theme.brand.sunup};
          ${
            active &&
            `
            background-color: ${theme.brand.sunup};
          `
          }
        `
      case 'accommodation':
      default:
        return `
          border: 1px solid ${theme.brand.jungle};
          ${
            active &&
            `
            background-color: ${theme.brand.jungle};
            color: white;
          `
          }
        `
    }
  }}
`

const TriggerIconWrapper = styled(BaseRoundIcon)`
  width: 40px;
  height: 40px;

  svg {
    width: 20px;
    height: 20px;
  }
`

const OptionIconWrapper = styled(BaseRoundIcon)`
  margin: 0 30px;
  width: 100px;
  height: 100px;

  svg {
    width: 40px;
    height: 40px;
  }
`

export const IconForSearchType = ({
  searchType,
}: {
  searchType: GQL_ListingTypes
}) => {
  switch (searchType) {
    case 'adventures':
      return <AdventureIcon />
    case 'accommodation':
    default:
      return <AccommodationIcon />
  }
}

interface SearchTypeToggleProps {
  open?: boolean
  setOpen: (_: boolean) => any
  currentSearchType: Maybe<GQL_ListingTypes>
  setPrimaryFilterType: (type: SearchType) => any
}

const SearchTypeToggle = ({
  open,
  setOpen,
  currentSearchType,
  setPrimaryFilterType,
}: SearchTypeToggleProps) => {
  const setTypeToAccommodation = () => setPrimaryFilterType('accommodation')
  const setTypeToAdventures = () => setPrimaryFilterType('adventures')

  const triggerRef = useRef<HTMLButtonElement>()
  const panelRef = useRef<HTMLDivElement>()

  const handleOutsideClick = (e: Event) => {
    const clickWasInsidePanel = panelRef.current?.contains(e.target as Node)
    const clickWasInsideTrigger = triggerRef.current?.contains(e.target as Node)

    if (!clickWasInsidePanel && !clickWasInsideTrigger) {
      setOpen(false)
    }
  }

  useDocumentClick(handleOutsideClick)

  const accommodationActive = currentSearchType === 'accommodation'
  const adventuresActive = currentSearchType === 'adventures'

  return (
    <>
      <InvisibleButton
        onClick={() => setOpen(!open)}
        ref={triggerRef}
        aria-label="Toggle view"
      >
        <TriggerIconWrapper searchType={currentSearchType} active>
          <IconForSearchType searchType={currentSearchType} />
        </TriggerIconWrapper>
      </InvisibleButton>
      <Panel open={open} ref={panelRef}>
        <OptionButton
          onClick={setTypeToAccommodation}
          aria-label="View all accommodation"
        >
          <OptionIconWrapper
            active={accommodationActive}
            searchType="accommodation"
          >
            <AccommodationIcon />
          </OptionIconWrapper>
          <p>Places to stay</p>
        </OptionButton>
        <OptionButton
          onClick={setTypeToAdventures}
          aria-label="View all adventures"
        >
          <OptionIconWrapper active={adventuresActive} searchType="adventures">
            <AdventureIcon />
          </OptionIconWrapper>
          <p>Things to do</p>
        </OptionButton>
      </Panel>
    </>
  )
}

export default SearchTypeToggle
